/**
 * Stale code after a redeploy.
 *
 * Vite names every lazy chunk after its content hash, and a new build deletes the old files. A tab
 * opened before the deploy still holds the old `index-*.js`, so the first lazy page it opens asks
 * for a chunk that no longer exists: nginx answers 404, the dynamic import rejects, and the page
 * said "The skeleton editor could not be opened". The cure is a plain reload, which fetches the new
 * index and its chunks — so do it once, automatically.
 *
 * The guard is per tab (sessionStorage) and time-boxed: a chunk that is *really* broken reloads at
 * most once a minute instead of looping, and the caller then shows its own failure note.
 */

const KEY = 'ss:stale-chunk-reload'
const WINDOW_MS = 60_000

let pending = false

/** A reload for new code is under way: render a quiet "Updating…" rather than an error. */
export function staleReloadPending(): boolean {
  return pending
}

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed|is not a valid JavaScript MIME type/i

/** True for the errors a missing or unfetchable lazy chunk produces (Chrome, Firefox, Safari). */
export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false
  const message = err instanceof Error ? `${err.name}: ${err.message}` : typeof err === 'string' ? err : ''
  return CHUNK_ERROR.test(message)
}

function lastReload(): number {
  try {
    return Number(sessionStorage.getItem(KEY) ?? 0) || 0
  } catch {
    return 0
  }
}

/**
 * Reload the page to pick up the new build, unless this tab already did so in the last minute.
 * Returns true when a reload was started (the caller should render a quiet "Updating…" state).
 */
export function reloadForStaleChunk(now: number = Date.now()): boolean {
  if (typeof window === 'undefined') return false
  if (pending) return true
  if (now - lastReload() < WINDOW_MS) return false
  pending = true
  try {
    sessionStorage.setItem(KEY, String(now))
  } catch {
    // No storage (private mode): still reload once; the next failure lands on the note.
  }
  window.location.reload()
  return true
}

/**
 * Vite fires `vite:preloadError` when a dynamic import's chunk (or its CSS) fails to load. Start the
 * reload then. The error is NOT swallowed: the importer's own catch still runs, sees
 * `staleReloadPending()` and draws "Updating…" for the moment until the page goes.
 */
export function installStaleChunkReload(win: Window = window): () => void {
  const onPreloadError = (): void => {
    reloadForStaleChunk()
  }
  win.addEventListener('vite:preloadError', onPreloadError)
  return () => win.removeEventListener('vite:preloadError', onPreloadError)
}
